import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import * as R from 'ramda'

import FilterValuesPopover from 'components/FilterValuesPopover'
import { FilterLabel } from 'components/FilterLabel'
import CustomFilterValueInputForIsComparison from 'components/CustomFilterValueInputForIsComparison'

/**
 * @template T
 * @typedef {import('shared/components/FilterGroup/reducer').Rule<T>} Rule
 */
/** @typedef {import('shared/components/FilterGroup/reducer').Filter} Filter */
/** @typedef {import('shared/components/FilterGroup/reducer').Comparison} Comparison */

const COMPARISON_OVERRIDES_BY_FIELD = {
  'topic.pair.sentiment': [
    { label: 'is', value: 'is', inputComponent: CustomFilterValueInputForIsComparison },
    { label: 'is not', value: 'is_not', inputComponent: CustomFilterValueInputForIsComparison },
  ],
  attachment: [
    { label: 'has any value', value: 'has_any_value', inputComponent: null },
    { label: 'is unknown', value: 'is_unknown', inputComponent: null },
  ],
  published: [
    { label: 'is true', value: 'has_any_value', inputComponent: null },
    { label: 'is false', value: 'is_unknown', inputComponent: null },
  ],
  'customer.subscribed': [
    { label: 'is true', value: 'has_any_value', inputComponent: null },
    { label: 'is false', value: 'is_unknown', inputComponent: null },
  ],
}

/**
 * @template T
 * @param {object} props
 * @param {Rule<T>} props.rule
 * @param {boolean} props.open
 * @param {function} props.onClick
 * @param {(filters: Filter[]) => void} props.onChangeValue
 * @param {(comparison: Comparison) => void} props.onComparisonChange
 * @param {function} props.onRemove
 * @param {() => void} props.onPopoverClose
 * @param {(data) => void} props.onExtraChange
 * @param {object[]} props.comparisons
 * @param {boolean=} props.newAdvancedFilter
 * @param {number=} props.index
 * @param {number=} props.rulesLength
 */
export const FilterItem = ({
  rule,
  open,
  onClick,
  onChangeValue,
  onRemove,
  onPopoverClose,
  onComparisonChange,
  comparisons,
  onExtraChange,
  newAdvancedFilter,
  index,
  rulesLength,
}) => {
  const [isError, setIsError] = useState(false)
  const [filterLabelRef, setFilterLabelRef] = useState(null)

  function filterLabelRefCallback(ref) {
    setFilterLabelRef(ref)
  }

  useEffect(() => {
    if (!open) {
      const error = R.isEmpty(rule.filters)
      setIsError(error)
    }
  }, [open, rule.filters])

  const fieldComparisons = overrideComparison(rule.field, comparisons)

  return (
    <Wrapper newAdvancedFilter={newAdvancedFilter} index={index} rulesLength={rulesLength}>
      <FilterLabel
        rule={rule}
        active={open}
        isValueMissing={isError}
        onClick={onClick}
        onRemove={onRemove}
        ref={filterLabelRefCallback}
        comparisons={fieldComparisons}
        newAdvancedFilter={newAdvancedFilter}
        rulesLength={rulesLength}
      />
      <FilterValuesPopover
        rule={rule}
        open={open}
        context={filterLabelRef}
        onClose={onPopoverClose}
        onChange={onChangeValue}
        onComparisonChange={onComparisonChange}
        comparisons={fieldComparisons}
        onExtraChange={onExtraChange}
      />
    </Wrapper>
  )
}

function overrideComparison(field, comparisons) {
  return COMPARISON_OVERRIDES_BY_FIELD[field] || comparisons
}

/**
 * @typedef {Object} WrapperProps
 * @property {boolean} [newAdvancedFilter]
 * @property {number} [index]
 * @property {number} [rulesLength]
 */

/**
 * Wrapper component with conditional styles.
 * @type {import('styled-components').StyledComponent<'div', any, WrapperProps, never>}
 */
const Wrapper = styled.div`
  display: inline-block;
  stroke: #dee6f0 !important;
  margin: ${(props) => (props.newAdvancedFilter ? '0px 0px' : '0px 0px 6px 0px;')};
  background-color: ${(props) => (props.newAdvancedFilter ? '#DEE6F0' : 'transparent')};
  padding: ${(props) => {
    if (props.newAdvancedFilter && props.rulesLength > 1) {
      return props.index === props.rulesLength - 1 ? '4px 0 4px 4px' : '4px'
    }
    return '0'
  }};
  border-top-left-radius: ${(props) => (props.index == 0 ? '4px' : '0')};
  border-bottom-left-radius: ${(props) => (props.index == 0 ? '4px' : '0')};
`



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/FilterItem/index.js